import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';

export type LocacoesDetalheKind =
  | 'criadas'
  | 'canceladas'
  | 'canceladas_lt90'
  | 'ativas'
  | 'canceladas_mes_ref';

export interface DiretoriaLocacoesDetalheModalProps {
  open: boolean;
  onClose: () => void;
  kind: LocacoesDetalheKind | null;
  companyId?: string;
  periodFrom: string;
  periodTo: string;
  title?: string;
  subtitle?: string;
}

interface LocacoesDetalhePayload {
  kind: LocacoesDetalheKind;
  periodo: { dataFrom: string; dataTo: string };
  total: number;
  items: Array<{
    idLocacao: string;
    cliente: string;
    placa: string;
    modelo: string;
    empresa: string;
    produto: string;
    status: string;
    motivo: string;
    dataInicio: string | null;
    dataCancelamento: string | null;
    diasContrato: number | null;
  }>;
  querySql?: string;
}

const KIND_TITLES: Record<LocacoesDetalheKind, string> = {
  criadas: 'Locações criadas',
  canceladas: 'Cancelamentos',
  canceladas_lt90: 'Cancelamentos < 90 dias',
  ativas: 'Locações ativas',
  canceladas_mes_ref: 'Canceladas no mês (turnover)',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fmtPeriodo(from: string, to: string): string {
  return `${fmtDate(from)} → ${fmtDate(to)}`;
}

export const DiretoriaLocacoesDetalheModal: React.FC<DiretoriaLocacoesDetalheModalProps> = ({
  open,
  onClose,
  kind,
  companyId,
  periodFrom,
  periodTo,
  title,
  subtitle,
}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['diretoria-locacoes-detalhe', kind, companyId, periodFrom, periodTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('kind', kind!);
      if (companyId) params.set('companyId', companyId);
      if (periodFrom) params.set('dataFrom', periodFrom);
      if (periodTo) params.set('dataTo', periodTo);
      const res = await api.get(`/reports/diretoria/locacoes-detalhe?${params.toString()}`);
      return res.data.data as LocacoesDetalhePayload;
    },
    enabled: open && Boolean(kind),
    staleTime: 60_000,
    retry: 1,
  });

  if (!open || !kind) return null;

  const errMsg =
    isError && error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: string }).message ?? 'Falha ao carregar')
      : isError
        ? 'Falha ao carregar listagem de locações'
        : null;

  const showDias = kind === 'canceladas_lt90';
  const showCancel = kind !== 'criadas' && kind !== 'ativas';
  const heading = title ?? KIND_TITLES[kind];
  const periodoLabel = data?.periodo
    ? fmtPeriodo(data.periodo.dataFrom, data.periodo.dataTo)
    : fmtPeriodo(periodFrom, periodTo);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Fechar" onClick={onClose} />
      <Card className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden border-border shadow-lg">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 border-b border-border/80 pb-3">
          <div className="min-w-0">
            <CardTitle className="text-lg">{heading}</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {subtitle ?? 'PortalFly · locações'}
              {data?.total != null ? ` · ${data.total.toLocaleString('pt-BR')} registro(s)` : ''}
              {` · ${periodoLabel}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <DiretoriaSqlButton
              sql={data?.querySql}
              source="cobranca"
              title={`${heading} — SQL`}
            />
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-4">
          {isPending ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Carregando locações…</p>
          ) : errMsg ? (
            <p className="py-10 text-center text-sm text-rose-700">{errMsg}</p>
          ) : (data?.items.length ?? 0) === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Nenhuma locação neste filtro.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/80">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-muted-foreground">
                    <th className="px-2 py-1.5 font-medium">Cliente</th>
                    <th className="px-2 py-1.5 font-medium">Placa</th>
                    <th className="px-2 py-1.5 font-medium">Empresa</th>
                    <th className="px-2 py-1.5 font-medium">Produto</th>
                    <th className="px-2 py-1.5 font-medium">Início</th>
                    {showCancel ? (
                      <th className="px-2 py-1.5 font-medium">Cancelamento</th>
                    ) : null}
                    {showDias ? (
                      <th className="px-2 py-1.5 text-right font-medium">Dias</th>
                    ) : null}
                    <th className="px-2 py-1.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((row, i) => {
                    const colSpan = 5 + (showCancel ? 1 : 0) + (showDias ? 1 : 0) + 1;
                    return (
                      <React.Fragment key={`${row.idLocacao}-${i}`}>
                        <tr className="border-b border-border/40">
                          <td className="px-2 py-1.5 font-medium align-top">{row.cliente}</td>
                          <td className="px-2 py-1.5 tabular-nums align-top">{row.placa}</td>
                          <td className="px-2 py-1.5 text-muted-foreground align-top">
                            {row.empresa}
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground align-top">
                            {row.produto || row.modelo || '—'}
                          </td>
                          <td className="px-2 py-1.5 tabular-nums align-top">
                            {fmtDate(row.dataInicio)}
                          </td>
                          {showCancel ? (
                            <td className="px-2 py-1.5 tabular-nums align-top">
                              {fmtDate(row.dataCancelamento)}
                            </td>
                          ) : null}
                          {showDias ? (
                            <td className="px-2 py-1.5 text-right tabular-nums align-top">
                              {row.diasContrato != null ? row.diasContrato : '—'}
                            </td>
                          ) : null}
                          <td className="px-2 py-1.5 align-top">{row.status}</td>
                        </tr>
                        {showCancel ? (
                          <tr className="border-b border-border/50 bg-muted/15">
                            <td
                              colSpan={colSpan}
                              className="px-3 py-2 text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words"
                            >
                              <span className="font-semibold text-muted-foreground">Motivo: </span>
                              {row.motivo?.trim() || '—'}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
